import React, { useEffect, useRef, useState } from 'react';
import { Popover, Textarea, TextInput } from '@mantine/core';
import classes from './EditableCell.module.css';

interface EditableCellProps {
    value: string | null | undefined;
    pendingValue?: string;
    onPendingChange: (newValue: string) => void;
    /**
     * Edit in a popover rather than inside the cell.
     *
     * The in-cell input is capped at the column's width, and this grid declares none — so
     * every column gets the same share and Street gets as much room as Country, which holds
     * "India". Fine for the short values that is true of, useless for an address: the
     * longest live street is 171 characters and the longest vendor payment terms 356, of
     * which about fifteen are visible while typing.
     *
     * Only long-text columns opt in. A popover to change a zip or a two-letter country code
     * would be heavier than the thing it replaces, so the default path below is deliberately
     * untouched — this is an extra branch, not a rewrite of the existing one.
     */
    wide?: boolean;
    /**
     * `wide` only: whether THIS cell is the one being edited.
     *
     * Owned by the grid, not the cell, and that is the whole point. When each cell kept its
     * own boolean, nothing told the first popover to close when a second one opened, so two
     * could sit open at once — which is exactly what happened. One value at the grid level
     * makes that impossible: opening B is the same action as closing A. The column-filter
     * popover in DataSheet has always worked this way.
     */
    isOpen?: boolean;
    /** `wide` only: ask the grid to open or close this cell's editor. */
    onOpenChange?: (open: boolean) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
    value,
    pendingValue,
    onPendingChange,
    wide = false,
    isOpen = false,
    onOpenChange,
}) => {
    const displayValue = pendingValue ?? value ?? '';
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(displayValue);
    const inputRef = useRef<HTMLInputElement>(null);
    const areaRef = useRef<HTMLTextAreaElement>(null);
    /*
     * Closing can be reached twice for one edit — the textarea blurs AND the popover
     * reports a click outside. Without this the second pass fires `onPendingChange` again
     * for a cell that already has a pending value.
     */
    const committedRef = useRef(false);
    const isPending = pendingValue !== undefined;

    useEffect(() => {
        if (!editing) {
            setDraft(pendingValue ?? value ?? '');
        }
    }, [value, pendingValue, editing]);

    // Seed the draft when the grid opens this cell, and focus the textarea it just mounted.
    useEffect(() => {
        if (!wide || !isOpen) return;
        committedRef.current = false;
        setDraft(pendingValue ?? value ?? '');
        const id = setTimeout(() => areaRef.current?.focus(), 0);
        return () => clearTimeout(id);
        // Only on the open transition: re-seeding mid-edit would discard what is being typed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wide, isOpen]);

    const startEdit = () => {
        if (wide) {
            onOpenChange?.(true);
            return;
        }
        setDraft(pendingValue ?? value ?? '');
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const commitEdit = () => {
        setEditing(false);
        const dbValue = value ?? '';
        if (draft !== dbValue || isPending) {
            onPendingChange(draft);
        }
    };

    const commitWide = () => {
        if (committedRef.current) return;
        committedRef.current = true;
        const dbValue = value ?? '';
        if (draft !== dbValue || isPending) {
            onPendingChange(draft);
        }
        onOpenChange?.(false);
    };

    const cancelWide = () => {
        committedRef.current = true;
        setDraft(pendingValue ?? value ?? '');
        onOpenChange?.(false);
    };

    const readView = (
        <div
            className={`${classes.readView} ${isPending ? classes.pending : ''}`}
            onClick={startEdit}
            title={displayValue || undefined}
        >
            {displayValue || <span className={classes.empty}>—</span>}
        </div>
    );

    if (wide) {
        /*
         * The Popover is mounted ONLY for the cell being edited. Previously every wide cell
         * in every row mounted one — fifty rows of vendors meant a hundred popovers, each
         * with its own document listeners and positioning, and the frame rate halved.
         *
         * No `trapFocus`: Settings is itself a Mantine Modal with its own focus trap, and
         * this dropdown is portalled outside that subtree. Two traps fighting over one
         * portalled node is why closing stopped working. The DataSheet filter popover, which
         * has always worked in here, does not use it either.
         *
         * A textarea, not an input: these values are addresses and terms that genuinely want
         * line breaks. Enter therefore inserts one; the edit commits on blur or with
         * Cmd/Ctrl+Enter, and Escape abandons it.
         */
        if (!isOpen) return readView;

        return (
            <Popover
                opened
                onClose={commitWide}
                position="bottom-start"
                withinPortal
                shadow="md"
                radius="md"
            >
                <Popover.Target>{readView}</Popover.Target>
                <Popover.Dropdown p={6}>
                    <Textarea
                        ref={areaRef}
                        value={draft}
                        onChange={(e) => setDraft(e.currentTarget.value)}
                        onBlur={commitWide}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelWide();
                            }
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                e.preventDefault();
                                commitWide();
                            }
                        }}
                        autosize
                        minRows={3}
                        maxRows={8}
                        w={420}
                        size="xs"
                    />
                </Popover.Dropdown>
            </Popover>
        );
    }

    if (editing) {
        return (
            <TextInput
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') inputRef.current?.blur();
                    if (e.key === 'Escape') {
                        setDraft(pendingValue ?? value ?? '');
                        setEditing(false);
                    }
                }}
                size="xs"
                w="100%"
            />
        );
    }

    return readView;
};

export default EditableCell;
